import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { userApi } from "../../api";
import { UserIdPayload } from "./common-payloads";
import {
  ColorPayload,
  DisplayNamePayload,
  EmailPayload,
  ProfileImagePayload,
} from "./user-payloads";
import { signOut } from "../common-actions";

interface UserState {
  username: string;
  displayName: string;
  color: string;
  email: string;
  id: string;
  verified: boolean;
  profileImageUrl: string;
  createdAt: string;
  hasBastionAccess: boolean;
  premiumExpiresAt: string;
  loading: boolean;
  blockedUserIds: {
    [userId: string]: true;
  };
  mutedUserIds: {
    [userId: string]: true;
  };
}

const initialState: UserState = {
  username: "",
  displayName: "",
  color: "graphite",
  email: "",
  id: "",
  verified: false,
  profileImageUrl: "",
  createdAt: "",
  hasBastionAccess: false,
  premiumExpiresAt: "",
  loading: true,
  blockedUserIds: {},
  mutedUserIds: {},
};

const authenticate = createAsyncThunk("authenticate", async () => {
  const res = await userApi.getUser();
  return res.data;
});

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    verify: (state) => {
      state.verified = true;
    },
    profileImageUpdated: (
      state,
      action: PayloadAction<ProfileImagePayload>
    ) => {
      state.profileImageUrl = action.payload.profileImageUrl;
    },
    emailUpdated: (state, action: PayloadAction<EmailPayload>) => {
      state.email = action.payload.email;
    },
    displayNameUpdated: (state, action: PayloadAction<DisplayNamePayload>) => {
      state.displayName = action.payload.displayName;
    },
    colorUpdated: (state, action: PayloadAction<ColorPayload>) => {
      state.color = action.payload.color;
    },
    blockedUser: (state, action: PayloadAction<UserIdPayload>) => {
      state.blockedUserIds[action.payload.userId] = true;
    },
    unblockedUser: (state, action: PayloadAction<UserIdPayload>) => {
      delete state.blockedUserIds[action.payload.userId];
    },
    mutedUser: (state, action: PayloadAction<UserIdPayload>) => {
      state.mutedUserIds[action.payload.userId] = true;
    },
    unmutedUser: (state, action: PayloadAction<UserIdPayload>) => {
      delete state.mutedUserIds[action.payload.userId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authenticate.fulfilled, (state, action) => {
      const {
        id,
        email,
        username,
        display_name,
        color,
        profile_image_url,
        created_at,
        verified,
        premium_expires_at,
        has_bastion_access,
      } = action.payload;

      state.id = id;
      state.email = email;
      state.username = username;
      state.displayName = display_name;
      state.color = color;
      state.profileImageUrl = profile_image_url;
      state.createdAt = created_at;
      state.hasBastionAccess = has_bastion_access;
      state.verified = verified;
      state.premiumExpiresAt = premium_expires_at;
      state.loading = false;
    });

    builder.addCase(authenticate.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(signOut, () => {
      return { ...initialState, loading: false }; // https://redux-toolkit.js.org/usage/immer-reducers#resetting-and-replacing-state
    });
  },
});

export const {
  verify,
  profileImageUpdated,
  emailUpdated,
  displayNameUpdated,
  colorUpdated,
} = userSlice.actions;

export { authenticate };

// export const selectUsername = (state: RootState) => state.auth.

export default userSlice.reducer;
